<template>
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-4 left">
          <div class="title">
            {{ $t('sidebar.inventory') }} > {{ $t('sidebar.inventorybehavior') }}
          </div>
        </div>
        <div class="col-md-8 right">
          <div class="action">
            <base-filter-data-type
              :type="dataType"
              :items="REPORT_DATA_TYPES"
              @onSelect="handleSelectDataType" />
            <base-dropdown-filter-download
              :items="DOWNLOAD_EXTS"
              :loading="false"
              @onSelect="handleSelectDownload" />
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: dateStart, end: dateEnd }"
              :active="isAppliedFilter"
              @onSubmit="handleSubmitFilter">
              <template #add>
                <div class="row">
                  <div class="col-12">
                    <hr>
                  </div>
                  <div class="col-12">
                    <section-title
                      :title="`${$t('message.selectwarehouse')}:`"
                      fontweight="bold" />
                    <base-dropdown
                      v-model="whSelected"
                      :options="whOptions" />
                  </div>
                </div>
              </template>
            </base-dropdown-filter-date-range>
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <div
        v-else
        class="col-md-12 no-padding mt-2">
        <template v-if="1">
          <section class="mt-2 mb-4">
            <div>
              <section-title
                :title="datePeriod(dateStart, dateEnd)"
                fontweight="bold"/>
            </div>
          </section>
          <keep-alive>
            <report-inventory-table
              v-if="dataType === REPORT_DATA_TYPE_VALUE.TABLE"
              :items="inventoryStocks"
              :columnNames="getAvailableData.map((item) => ({
                style: item.style,
                text: item.text,
              }))"
              :pickKeys="getAvailableData.map((item) => (
                `totalBehavior.${item.pickKey}`
              ))"
            />
            <report-inventory-chart
              v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_BAR"
              :items="inventoryStocks"
              :selectedLabels.sync="chartSelectedLabels"
              :mapLabels="getAvailableData.reduce((acc, cur) => {
                return {
                  [`totalBehavior.${cur.pickKey}`]: cur.text,
                  ...acc,
                }
              }, {})"
              :showLegendLabels="true"
              :showAxisLabelCurrency="false"
              :areaStyle="null"
              type="bar" />
            <report-inventory-chart
              v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_LINE"
              :items="inventoryStocks"
              :selectedLabels.sync="chartSelectedLabels"
              :mapLabels="getAvailableData.reduce((acc, cur) => {
                return {
                  [`totalBehavior.${cur.pickKey}`]: cur.text,
                  ...acc,
                }
              }, {})"
              :showLegendLabels="true"
              :showAxisLabelCurrency="false"
              :areaStyle="null"
              type="line" />
          </keep-alive>
        </template>
        <template v-else>
          {{ $t('message.nodatafound') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE } from '../../../../helpers/enum'
import { getDateStart, getDateEnd, getLastSevenDay } from '../../../../helpers'
import sidebarModule from './../../sidebarModule'

import XLSX from 'xlsx'
import { getFileNameStockInventoryBehavior } from '@/helpers/exportFormater'

const LAST_SEVEN_DAY = getLastSevenDay()

export default {
  name: 'ReportInventoryBehavior',

  data () {
    return {
      REPORT_DATA_TYPES,
      REPORT_DATA_TYPE_VALUE,
      DOWNLOAD_EXTS,
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      dateStart: LAST_SEVEN_DAY.START,
      dateEnd: LAST_SEVEN_DAY.END,
      dataType: REPORT_DATA_TYPE_VALUE.TABLE,
      chartSelectedLabels: {},
      whSelected: null,
      isAppliedFilter: false,
    }
  },

  computed: {
    ...mapGetters({
      warehouses: 'getWarehouses',
      dateformat: 'getDateFormat',
      products: 'getProducts',
      getProductFullNameBySkuId: 'getProductFullNameBySkuId',
    }),
    whOptions () {
      return this.warehouses.map((item) => {
        return {
          text: item.warehouse_name,
          value: item.warehouse_id,
        }
      })
    },
    inventoryStocks () {
      return this.$store.state.report.inventoryStocks.map((item) => {
        return {
          ...item,
          r_date: item.date,
          date: (() => {
            if (this.dateType === FILTER_DATE_TYPE.DAY) {
              return this.getMyLocalDate(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.MONTH) {
              return this.getMyLocalMonthYear(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.YEAR) {
              return this.getMyLocalYear(item.date, { short: false })
            }
            return item.date
          })(),
          skus: item.skus.map((sku) => {
            const productName = this.getProductFullNameBySkuId(sku.info.skuId)
            return {
              ...sku,
              info: {
                ...sku.info,
                productName,
              },
            }
          }),
        }
      })
    },
    getAvailableData() {
      const columnsMapData = [
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasongr'),
          pickKey: 'gr',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasongi'),
          pickKey: 'gi',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasongt'),
          pickKey: 'gt',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonpos'),
          pickKey: 'pos',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonrecipe'),
          pickKey: 'recipe',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualinitial'),
          pickKey: 'manualInitial',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualrecount'),
          pickKey: 'manualRecount',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualreceived'),
          pickKey: 'manualReceived',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualissue'),
          pickKey: 'manualIssue',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualreturn'),
          pickKey: 'manualReturn',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualloss'),
          pickKey: 'manualLoss',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualdamage'),
          pickKey: 'manualDamage',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualtransfer'),
          pickKey: 'manualTransfer',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualcreatelotnumber'),
          pickKey: 'manualCreateLotNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualupdatelotnumber'),
          pickKey: 'manualUpdateLotNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualdeletelotnumber'),
          pickKey: 'manualDeleteLotNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualcreateserialnumber'),
          pickKey: 'manualCreateSerialNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualupdateserialnumber'),
          pickKey: 'manualUpdateSerialNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualdeleteserialnumber'),
          pickKey: 'manualDeleteSerialNumber',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualconsumedproduction'),
          pickKey: 'manualConsumedProduction',
        },
        {
          style: { 'min-width': '80px' },
          text: this.$t('status.inventory.reasonmanualreceivedproduction'),
          pickKey: 'manualReceivedProduction',
        },
      ]

      const columnsState = columnsMapData.map((v) => false)

      for (const data of this.inventoryStocks) {
        const keys = Object.keys(data.totalBehavior)
        for (const index in keys) {
          const key = keys[index]
          if (data.totalBehavior[key] > 0) {
            const itemIndex = columnsMapData.findIndex((item) => item.pickKey === key)
            columnsState[itemIndex] = true
          }
        }
      }

      return columnsMapData.reduce((acc, cur, idx) => {
        if (columnsState[idx] === true) {
          const itemIndex = columnsMapData.findIndex((item) => item.pickKey === columnsMapData[idx].pickKey)
          acc.push({
            style: columnsMapData[itemIndex].style,
            text: columnsMapData[itemIndex].text,
            pickKey: columnsMapData[itemIndex].pickKey,
          })
        }
        return acc
      }, [])
    },
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.reports.title'),
      current: {
        app_name: 'reports',
        app_id: 8,
        module_id: 3,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 4,
      submenuAtIdx: 3,
    })
  },

  async created () {
    if (this.warehouses.length == 0) {
      await this.$store.dispatch('getWarehouses')
    }
    if (this.products.length == 0) {
      await this.$store.dispatch('getProducts')
    }
    this.whSelected = this.warehouses.find((wh) => wh.primary === true).warehouse_id
    await this.fetchData()
  },

  destroyed: function () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.ready = false
      await this.$store.dispatch('getInventoryStocks', {
        'filter[date_start]': getDateStart(this.dateStart),
        'filter[date_end]': getDateEnd(this.dateEnd),
        'filter[date_type]': this.dateType,
        'filter[wh_id]': this.whSelected,
        'timezone': moment.tz.guess(),
        'type': 'behavior',
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handleSubmitFilter ({ type, start, end }) {
      this.dateType = type
      this.dateStart = start
      this.dateEnd = end
      this.isAppliedFilter = true
      this.fetchData()
    },
    handleSelectDataType ({ value = null }) {
      this.dataType = value
    },
    handleSelectDownload ({ value = null }) {
      if (value === DOWNLOAD_EXT_VALUE.EXCEL) {
        this.exportAs('xlsx')
      } else if (value === DOWNLOAD_EXT_VALUE.CSV) {
        this.exportAs('csv')
      }
    },
    async exportAs (fileType) {
      /* convert state to workbook */
      const headColumns = [
        this.$t('message.date'),
        this.$t('message.productlist'),
        this.$t('status.inventory.reasongr'),
        this.$t('status.inventory.reasongi'),
        this.$t('status.inventory.reasongt'),
        this.$t('status.inventory.reasonpos'),
        this.$t('status.inventory.reasonrecipe'),
        this.$t('status.inventory.reasonmanualinitial'),
        this.$t('status.inventory.reasonmanualrecount'),
        this.$t('status.inventory.reasonmanualreceived'),
        this.$t('status.inventory.reasonmanualissue'),
        this.$t('status.inventory.reasonmanualreturn'),
        this.$t('status.inventory.reasonmanualloss'),
        this.$t('status.inventory.reasonmanualdamage'),
        this.$t('status.inventory.reasonmanualtransfer'),
        this.$t('status.inventory.reasonmanualcreatelotnumber'),
        this.$t('status.inventory.reasonmanualupdatelotnumber'),
        this.$t('status.inventory.reasonmanualdeletelotnumber'),
        this.$t('status.inventory.reasonmanualcreateserialnumber'),
        this.$t('status.inventory.reasonmanualupdateserialnumber'),
        this.$t('status.inventory.reasonmanualdeleteserialnumber'),
        this.$t('status.inventory.reasonmanualconsumedproduction'),
        this.$t('status.inventory.reasonmanualreceivedproduction'),

      ]
      let bodyRows = new Array()

      for (let item of this.inventoryStocks) {
        for (let sku of item.skus) {
          bodyRows.push(
            [
              item.r_date,
              sku.info.productName,
              sku.totalBehavior.gr,
              sku.totalBehavior.gi,
              sku.totalBehavior.gt,
              sku.totalBehavior.pos,
              sku.totalBehavior.recipe,
              sku.totalBehavior.manualInitial,
              sku.totalBehavior.manualRecount,
              sku.totalBehavior.manualReceived,
              sku.totalBehavior.manualIssue,
              sku.totalBehavior.manualReturn,
              sku.totalBehavior.manualLoss,
              sku.totalBehavior.manualDamage,
              sku.totalBehavior.manualTransfer,
              sku.totalBehavior.manualCreateLotNumber,
              sku.totalBehavior.manualUpdateLotNumber,
              sku.totalBehavior.manualDeleteLotNumber,
              sku.totalBehavior.manualCreateSerialNumber,
              sku.totalBehavior.manualUpdateSerialNumber,
              sku.totalBehavior.manualDeleteSerialNumber,
              sku.totalBehavior.manualConsumedProduction,
              sku.totalBehavior.manualReceivedProduction,

            ]
          )
        }
      }

      const data = [headColumns, ...bodyRows]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet')

      /* generate file and send to client */
      const fileName = getFileNameStockInventoryBehavior(this.$t, fileType)

      XLSX.writeFile(wb, fileName)
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/report/inventory/Behavior.vue